import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface Product {
    id: number;
    name: string;
    category: string;
    brand_name: string | null;
    ean_code: string | null;
}

interface Offer {
    id: number;
    currency: string;
    min_quantity: number;
    x: number;
    y: number;
    width: number | null;
    height: number | null;
    prices?: Array<{
        id: number;
        price_type: {
            id: number;
            name: string;
            code: string;
        };
        value: number;
        starts_at?: string | null;
        ends_at?: string | null;
    }>;
    packaging?: {
        id: number;
        name: string;
        size_value: string | null;
        size_unit: string | null;
        multiplier: number;
    } | null;
}

interface ProductData {
    product: Product | null;
    offer: Offer | null;
}

const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    padding: "24px",
    background: "#111",
    minHeight: "100vh",
};

const cardStyle: React.CSSProperties = {
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8,
    padding: "24px",
    color: "#fff",
};

const titleStyle: React.CSSProperties = {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "16px",
    color: "#fff",
};

const labelStyle: React.CSSProperties = {
    fontSize: "12px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#bbb",
    marginBottom: "8px",
    display: "block",
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "4px",
    color: "#fff",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "12px",
};

const buttonStyle: React.CSSProperties = {
    padding: "12px 20px",
    background: "rgba(59, 130, 246, 0.2)",
    border: "1px solid rgba(59, 130, 246, 0.5)",
    borderRadius: "4px",
    color: "#3b82f6",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
};

const valueStyle: React.CSSProperties = {
    color: "#ddd",
    fontSize: "14px",
    padding: "12px",
    backgroundColor: "rgba(255,255,255,0.02)",
    borderRadius: "4px",
    border: "1px solid rgba(255,255,255,0.06)",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
};

export default function ProductIdentifierView() {
    const [searchParams] = useSearchParams();
    const [productData, setProductData] = useState<ProductData | null>(null);
    const [loading, setLoading] = useState(true);
    const [editedData, setEditedData] = useState<Partial<Offer> | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const offerId = searchParams.get("offer_id");

    useEffect(() => {
        if (!offerId) {
            setLoading(false);
            return;
        }

        const fetchProductData = async () => {
            try {
                const res = await fetch(`/api/admin/product_identifiers/${offerId}`);
                const data = await res.json();
                if (data.success) {
                    setProductData(data.product_data);
                    setEditedData({
                        currency: data.product_data?.offer?.currency,
                        min_quantity: data.product_data?.offer?.min_quantity,
                    });
                }
            } catch (error) {
                console.error("Error fetching product data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProductData();
    }, [offerId]);

    const handleSave = async () => {
        if (!offerId || !editedData) return;

        setIsSaving(true);
        try {
            const res = await fetch(`/api/admin/product_identifiers/${offerId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editedData),
            });

            if (res.ok) {
                // Refresh data
                const data = await res.json();
                if (data.product_data) {
                    setProductData(data.product_data);
                }
            }
        } catch (error) {
            console.error("Error saving product data:", error);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>Betöltés…</div>
            </div>
        );
    }

    if (!productData) {
        return (
            <div style={containerStyle}>
                <div style={cardStyle}>Nem található termék</div>
            </div>
        );
    }

    const { product, offer } = productData;

    return (
        <div style={containerStyle}>
            {product && (
                <div style={cardStyle}>
                    <div style={titleStyle}>{product.name}</div>

                    <label style={labelStyle}>Kategória</label>
                    <div style={valueStyle}>{product.category}</div>

                    {product.brand_name && (
                        <>
                            <label style={labelStyle}>Márka</label>
                            <div style={valueStyle}>{product.brand_name}</div>
                        </>
                    )}

                    {product.ean_code && (
                        <>
                            <label style={labelStyle}>EAN Kód</label>
                            <div style={valueStyle}>{product.ean_code}</div>
                        </>
                    )}
                </div>
            )}

            {offer && (
                <div style={cardStyle}>
                    <div style={titleStyle}>Ajánlat Szerkesztése</div>

                    <label style={labelStyle}>Valuta</label>
                    <input
                        type="text"
                        style={inputStyle}
                        value={editedData?.currency || ""}
                        onChange={(e) => setEditedData(prev => ({ ...prev, currency: e.target.value }))}
                        placeholder="Valuta"
                    />

                    <label style={labelStyle}>Minimális Mennyiség</label>
                    <input
                        type="number"
                        style={inputStyle}
                        value={editedData?.min_quantity || ""}
                        onChange={(e) => setEditedData(prev => ({ ...prev, min_quantity: parseInt(e.target.value) }))}
                        placeholder="Min. mennyiség"
                    />

                    {offer.packaging && (
                        <>
                            <label style={labelStyle}>Kiszerelés</label>
                            <div style={valueStyle}>
                                {offer.packaging.size_value} {offer.packaging.size_unit}
                            </div>
                        </>
                    )}

                    <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
                        <button style={buttonStyle} onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Mentés…" : "Mentés"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
